<script setup>
import {
    ref,
    computed,
    watch,
    onMounted,
    nextTick,
    getCurrentInstance
} from 'vue';

const props = defineProps({
    data: Object,
});

// Instance & Store Access
const { proxy } = getCurrentInstance();
const store = proxy.$store;

// State
const show = ref(false);
const value = ref(props.data.code);
const codeMirrorInstance = ref(undefined);
const codeMirrorRef = ref(null);

// Computed Properties
const selections = computed(() => {
    return store.state.themes.findModalSelections;
});

const theme = computed(() => {
    return store.getters['user/pref']('codeMirror_theme');
});

const lineNumbers = computed(() => {
    return store.getters['user/pref']('codeMirror_lineNumbers');
});

const lineWrapping = computed(() => {
    return store.getters['user/pref']('codeMirror_lineWrapping');
});

const twigMode = computed(() => {
    return store.getters['user/pref']('codeMirror_twigMode');
});

// Watchers
watch(theme, (newValue) => {
    codeMirrorInstance.value?.setOption('theme', newValue);
});

watch(lineNumbers, (newValue) => {
    codeMirrorInstance.value?.setOption('lineNumbers', newValue);
});

watch(lineWrapping, (newValue) => {
    codeMirrorInstance.value?.setOption('lineWrapping', newValue);
});

watch(twigMode, (newValue) => {
    if (props.data.mode === 'text/html' || props.data.mode === 'twig') {
        props.data.mode = newValue ? 'twig' : 'text/html';
    }
    codeMirrorInstance.value?.setOption('mode', props.data.mode);
});

// Internal Methods
const setSelections = () => {
    if (selections.value.length) {
        // Highlight the first selection only to prevent multi-instance replacement
        const selectionToApply = [selections.value[0]];
        codeMirrorInstance.value.setSelections(selectionToApply);
        store.commit('themes/findModalSelections', []);
    }
};

const setCodeMirrorOptions = () => {
    if (props.data.history) {
        codeMirrorInstance.value.setHistory(props.data.history);
    } else {
        props.data.originalCode = codeMirrorInstance.value.getValue();
    }

    if (props.data.scrollInfo) {
        codeMirrorInstance.value.scrollTo(props.data.scrollInfo.left, props.data.scrollInfo.top);
    }

    // Replace tabs with spaces
    codeMirrorInstance.value.setOption('extraKeys', {
        Tab: (cm) => {
            const spaces = Array(cm.getOption('indentUnit') + 1).join(' ');
            cm.replaceSelection(spaces);
        }
    });
};

const setCodeMirrorEvents = () => {
    codeMirrorInstance.value.on('scroll', () => {
        props.data.scrollInfo = codeMirrorInstance.value.getScrollInfo();
    });

    codeMirrorInstance.value.on('change', () => {
        props.data.history = codeMirrorInstance.value.getHistory();
        props.data.code = codeMirrorInstance.value.getValue();
        props.data.isModified = JSON.stringify(props.data.originalCode) !== JSON.stringify(props.data.code);
    });

    codeMirrorInstance.value.on('focus', () => {
        props.data.focused = true;
        store.commit('themes/codeMirror', codeMirrorInstance.value);
    });

    codeMirrorInstance.value.on('blur', () => {
        props.data.focused = false;
    });
};

// Lifecycle Hooks
onMounted(() => {
    let codeMirrorOptions = {
        mode: props.data.mode,
        lineNumbers: true,
        lineWrapping: false,
        readOnly: props.data.readOnly,
        indentUnit: 4,
        tabMode: 'spaces',
        enterMode: 'indent',
        electricChars: false,
        autofocus: true,
        styleActiveLine: { nonEmpty: true },
        autoCloseTags: true
    };

    if (theme.value) {
        codeMirrorOptions['theme'] = theme.value;
    }

    if (lineNumbers.value !== undefined) {
        codeMirrorOptions['lineNumbers'] = lineNumbers.value;
    }

    if (lineWrapping.value !== undefined) {
        codeMirrorOptions['lineWrapping'] = lineWrapping.value;
    }

    if (twigMode.value !== undefined) {
        if (props.data.mode === 'text/html' || props.data.mode === 'twig') {
            props.data.mode = twigMode.value ? 'twig' : 'text/html';
        }
        codeMirrorOptions['mode'] = props.data.mode;
    }

    if (props.data.mode === 'javascript' || props.data.mode === 'css') {
        Object.assign(codeMirrorOptions, {
            gutters: ['CodeMirror-lint-markers'],
            lint: true
        });
    }

    show.value = true;

    nextTick(() => {
        // Note: Assumes CodeMirror is globally available via script tag as per original
        codeMirrorInstance.value = CodeMirror.fromTextArea(codeMirrorRef.value, codeMirrorOptions);
        codeMirrorInstance.value.setSize('100%', '100%');

        setSelections();
        setCodeMirrorOptions();
        setCodeMirrorEvents();
    });
});
</script>

<template>
    <textarea v-if="show" ref="codeMirrorRef">{{ value }}</textarea>
</template>